import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import Checkbox from '@/components/atoms/Checkbox';
import TextArea from '@/components/atoms/TextArea';
import { renderMarkdown } from '@/utils/markdown';

import styles from './BioscopeTestSettings.module.scss';

export interface BioscopeTestSettingsProps {
    isTestBioscope?: boolean;
    description?: string | null;
    onSave: (isTestBioscope: boolean, description: string | null) => Promise<void>;
    isLoading?: boolean;
}

function BioscopeTestSettings({
    isTestBioscope = false,
    description = null,
    onSave,
    isLoading = false,
}: BioscopeTestSettingsProps) {
    const { t } = useTranslation();
    const [isChecked, setIsChecked] = useState(isTestBioscope);
    const [localDescription, setLocalDescription] = useState(description || '');
    const [isSaving, setIsSaving] = useState(false);
    const [showPreview, setShowPreview] = useState(false);

    const markdownPreview = useMemo(
        () => (localDescription ? renderMarkdown(localDescription) : ''),
        [localDescription]
    );

    useEffect(() => {
        setIsChecked(isTestBioscope);
    }, [isTestBioscope]);

    useEffect(() => {
        setLocalDescription(description || '');
        setShowPreview(false); // Reset to edit mode when description changes
    }, [description]);

    const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setLocalDescription(e.target.value);
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const valueToSave = localDescription.trim() === '' ? null : localDescription.trim();
            await onSave(isChecked, valueToSave);
        } catch {
            // The page reports the failure (its mutation shows a toast); a rethrow here would
            // only reach the click handler as an unhandled rejection.
            setIsChecked(isTestBioscope);
            setLocalDescription(description || '');
        } finally {
            setIsSaving(false);
        }
    };

    const hasChanges =
        isChecked !== isTestBioscope || localDescription.trim() !== (description || '').trim();

    return (
        <div className={styles.BioscopeTestSettings}>
            <h2>{t('bioscopeDetail.testSettings.title')}</h2>
            <div className={styles.formGroup}>
                <Checkbox
                    label={t('bioscopeDetail.testSettings.isTest')}
                    rowClassName={styles.checkboxLabel}
                    checked={isChecked}
                    onCheckedChange={setIsChecked}
                    disabled={isLoading || isSaving}
                />
            </div>
            <div className={styles.formGroup}>
                <div className={styles.descriptionHeader}>
                    <label className={styles.textareaLabel}>
                        <span className={styles.labelText}>
                            {t('molecules.common.description')}
                        </span>
                        <span className={styles.markdownHint}>
                            {t('bioscopeDetail.testSettings.markdownSupported')}
                        </span>
                    </label>
                    <Button
                        variant="secondary"
                        size="small"
                        type="button"
                        onClick={() => setShowPreview(!showPreview)}
                        disabled={isLoading || isSaving || !localDescription.trim()}>
                        {showPreview
                            ? t('common.actions.edit')
                            : t('bioscopeDetail.testSettings.preview')}
                    </Button>
                </div>
                {/* Preview is only enabled for a description with text, so it is never empty. */}
                {showPreview ? (
                    <div
                        className={styles.markdownPreview}
                        // biome-ignore lint/security/noDangerouslySetInnerHtml: Markdown rendering requires HTML
                        dangerouslySetInnerHTML={{ __html: markdownPreview }}
                    />
                ) : (
                    <TextArea
                        value={localDescription}
                        onChange={handleTextareaChange}
                        disabled={isLoading || isSaving}
                        placeholder={t('bioscopeDetail.testSettings.descriptionPlaceholder')}
                        rows={5}
                        className={styles.textarea}
                    />
                )}
            </div>
            <div className={styles.buttonContainer}>
                <Button
                    type="button"
                    onClick={handleSave}
                    disabled={isLoading || isSaving || !hasChanges}>
                    {isSaving ? t('bioscopeDetail.testSettings.saving') : t('common.actions.save')}
                </Button>
            </div>
        </div>
    );
}

export default BioscopeTestSettings;
export { BioscopeTestSettings };
